import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { formatOrderNumber } from "@shared/orderUtils";
import { MessageCircle, Send, Loader2, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { SEO } from "@/components/seo";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface OrderItem {
  id: number;
  productId: number;
  product: { name: string } | null;
}

interface OrderDetail {
  id: number;
  createdAt: string;
  total: string;
  status: string;
  items: OrderItem[];
}

interface OrderMessage {
  id: number;
  orderId: number;
  senderType: string;
  body: string;
  readByCustomer: boolean;
  createdAt: string | null;
}

function OrderThread({ order, defaultOpen }: { order: OrderDetail; defaultOpen?: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(!!defaultOpen);
  const [replyBody, setReplyBody] = useState("");

  const { data: unreadCounts = {} as Record<number, number> } = useQuery<Record<number, number>>({
    queryKey: ["/api/orders/unread-counts"],
  });

  const unread = (unreadCounts as Record<number, number>)[order.id] || 0;

  const { data: messages = [], isLoading } = useQuery<OrderMessage[]>({
    queryKey: ["/api/orders", order.id, "messages"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/orders/${order.id}/messages`);
      return res.json();
    },
    enabled: open,
    refetchInterval: open ? 15000 : false,
  });

  const markReadMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/orders/${order.id}/messages/read`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/orders/unread-counts"] });
    },
  });

  const sendMutation = useMutation({
    mutationFn: async (body: string) => {
      const res = await apiRequest("POST", `/api/orders/${order.id}/message`, { body });
      return res.json();
    },
    onSuccess: () => {
      setReplyBody("");
      queryClient.invalidateQueries({ queryKey: ["/api/orders", order.id, "messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/orders/unread-counts"] });
      toast({ title: "Message sent" });
    },
    onError: () => {
      toast({ title: "Failed to send message", variant: "destructive" });
    },
  });

  function handleToggle() {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      markReadMutation.mutate();
    }
  }

  const productNames = order.items
    .map((i) => i.product?.name)
    .filter(Boolean)
    .join(", ");

  return (
    <Card>
      <CardHeader className="pb-3 cursor-pointer" onClick={handleToggle}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              {formatOrderNumber(order.id)}
              {unread > 0 && !open && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
                  {unread}
                </span>
              )}
            </CardTitle>
            {productNames && (
              <p className="text-sm text-muted-foreground truncate mt-0.5">{productNames}</p>
            )}
            <p className="text-xs text-muted-foreground mt-0.5">
              {new Date(order.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <MessageCircle className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{open ? "▲" : "▼"}</span>
          </div>
        </div>
      </CardHeader>

      {open && (
        <CardContent className="pt-0">
          <div className="border-t pt-4">
            {isLoading ? (
              <div className="space-y-2 mb-3">
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-10 w-1/2 ml-auto" />
              </div>
            ) : messages.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto mb-4 pr-1">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.senderType === "customer" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                        msg.senderType === "customer"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground border"
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.body}</p>
                      <p
                        className={`text-[10px] mt-1 ${
                          msg.senderType === "customer"
                            ? "text-primary-foreground/70"
                            : "text-muted-foreground"
                        }`}
                      >
                        {msg.senderType === "customer" ? "You" : "Support"} ·{" "}
                        {new Date(msg.createdAt!).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-4">
                No messages yet. Send us a message below.
              </p>
            )}

            <div className="flex gap-2">
              <Textarea
                placeholder="Send a message about this order… (Ctrl+Enter to send)"
                rows={2}
                className="flex-1 resize-none text-sm"
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && replyBody.trim()) {
                    sendMutation.mutate(replyBody.trim());
                  }
                }}
              />
              <Button
                size="sm"
                className="self-end"
                disabled={!replyBody.trim() || sendMutation.isPending}
                onClick={() => sendMutation.mutate(replyBody.trim())}
              >
                {sendMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function MessagesPage() {
  const [, navigate] = useLocation();
  const { user, isLoading: authLoading } = useAuth();

  const { data: orders, isLoading: ordersLoading } = useQuery<OrderDetail[]>({
    queryKey: ["/api/orders/my"],
    enabled: !!user,
  });

  // Keep unread counts warm with polling
  useQuery<Record<number, number>>({
    queryKey: ["/api/orders/unread-counts"],
    enabled: !!user,
    refetchInterval: 20000,
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [authLoading, user, navigate]);

  if (authLoading || !user) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Messages" description="View your support messages." noIndex />
        <Skeleton className="h-9 w-36 mb-8" />
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-40 mb-2" />
                <Skeleton className="h-4 w-64" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (ordersLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Messages" description="View your support messages." noIndex />
        <h1 className="text-2xl sm:text-3xl font-bold mb-8">Messages</h1>
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-40 mb-2" />
                <Skeleton className="h-4 w-64" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const hasOrders = orders && orders.length > 0;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO title="Messages" description="View your support messages." noIndex />

      <h1 className="text-2xl sm:text-3xl font-bold mb-2">Messages</h1>
      <p className="text-muted-foreground mb-8">
        Send a message to support about any of your orders.
      </p>

      {!hasOrders ? (
        <div className="max-w-md mx-auto text-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground mb-6">
            You don't have any orders yet. Messages are tied to orders — once you make a purchase
            you can message support here.
          </p>
          <Link href="/">
            <Button>Browse Products</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <OrderThread key={order.id} order={order} />
          ))}
        </div>
      )}
    </div>
  );
}
